import { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import './App.css';

import { createInitialState, WORLD_WIDTH, WORLD_HEIGHT, PLAYER_SIZE, ENEMY_SIZE, ENEMY_DAMAGE, POTION_COOLDOWN, POTION_HEAL_PCT, POTION_MAX, POTION_COST } from './constants';
import { applyEquippedItems, getSellPrice } from './data/items';
import { applyTalents }                      from './data/talentTree';
import { useKeyboard }                      from './hooks/useKeyboard';
import { useGameLoop }                      from './hooks/useGameLoop';
import { useBackgroundMusic }              from './hooks/useBackgroundMusic';
import { useInventory }                     from './hooks/useInventory';
import { usePersistentProgress }            from './hooks/usePersistentProgress';
import { LandingScreen }                    from './components/screens/LandingScreen';
import { IdleScreen }                       from './components/screens/IdleScreen';
import { SettingsScreen }                   from './components/screens/SettingsScreen';
import { DeadScreen }                       from './components/screens/DeadScreen';
import { CharacterScreen }                  from './components/screens/CharacterScreen';
import { EnhancementScreen }               from './components/screens/EnhancementScreen';
import { TalentTreeScreen }                 from './components/screens/TalentTreeScreen';
import { LeaderboardScreen }                from './components/screens/LeaderboardScreen';
import { GameWorld }                        from './components/organisms/GameWorld';

/**
 * App — thin orchestrator.
 * Owns top-level React state and refs, wires hooks and screens together.
 * All game logic lives in useGameLoop; all input handling in useKeyboard.
 */
function App() {
  const [gameState,         setGameState]         = useState('landing');
  const [, setRenderTick]                         = useState(0);
  const [attackRenderKey,   setAttackRenderKey]   = useState(0);
  const [levelUpSkills,     setLevelUpSkills]     = useState([]);
  const [magicSelectState,  setMagicSelectSpells]    = useState({ spells: [], isUpgrade: false });
  const [worldModifierChoices, setWorldModifierChoices] = useState([]);
  const [sessionDrops,      setSessionDrops]      = useState([]);
  const [sessionKills,      setSessionKills]      = useState(0);
  const [sessionGold,       setSessionGold]       = useState(0);
  const [chestLootOptions,  setChestLootOptions]  = useState([]);
  const [envAnnouncement,   setEnvAnnouncement]   = useState(null);
  const [showSettings,      setShowSettings]      = useState(false);
  const [showQuitModal,     setShowQuitModal]     = useState(false);

  const stateRef     = useRef(createInitialState());
  const lastSpawnRef = useRef(0);
  const startTimeRef = useRef(0);
  const pausedAtRef  = useRef(null);   // records when the game left 'playing' state
  const gameWorldRef = useRef(null);

  // ── Hooks ──────────────────────────────────────────────────────────────────
  const { inventory, equipped, equipItem, unequipItem, addDroppedItems, sellItem, sellAllItems, sellAllRunes, enhanceItem, lockItem, equipRuneWord, unequipAllRunes } = useInventory();
  const {
    totalGold, talentPoints, playerLevel, activatedTalents, leaderboard,
    lastDeathTier, potionCount, lootFilter,
    addGold, spendGold, addTalentPoints, addPlayerLevel, spendTalentPoints,
    activateTalent, resetTalents, addRunToLeaderboard, saveLastDeathTier,
    addPotion, consumePotion, setLootFilter,
  } = usePersistentProgress();

  // Memoised Set for O(1) lookup — passed to TalentTreeScreen + applyTalents
  const activatedSet = useMemo(() => new Set(activatedTalents), [activatedTalents]);

  // ── Background music ───────────────────────────────────────────────────────
  const { volume: musicVolume, muted: musicMuted, setVolume: setMusicVolume, toggleMute: toggleMusicMute } = useBackgroundMusic();

  // ── Auto-pause when the browser tab loses focus ───────────────────────────
  // Use a ref so the event listener never needs to be re-registered.
  const onTabHiddenRef = useRef(null);
  onTabHiddenRef.current = () => {
    setGameState(prev => {
      if (prev === 'playing') return 'paused';
      return prev;
    });
  };
  useEffect(() => {
    const onVisibility = () => { if (document.hidden) onTabHiddenRef.current?.(); };
    const onBlur       = () => { onTabHiddenRef.current?.(); };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('blur', onBlur);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('blur', onBlur);
    };
  }, []); // register once — ref always holds the latest callback

  // ── Tier-timer + spawn-gate pause accounting ─────────────────────────────
  // When the game leaves 'playing' (pause / spell select / chest / modifier),
  // record the wall-clock time.  On resume, advance every wall-clock gate by
  // the same gap so nothing fires on the first frame back:
  //   • startTimeRef          — keeps tier index / elapsed time correct
  //   • nextBossSpawnAt       — prevents instant boss spawn after any pause
  //   • tierBossWarning.startTime — preserves remaining warning countdown
  useEffect(() => {
    if (gameState !== 'playing') {
      pausedAtRef.current = performance.now();
    } else if (pausedAtRef.current !== null) {
      const gap = performance.now() - pausedAtRef.current;
      startTimeRef.current += gap;
      const s = stateRef.current;
      if (s.nextBossSpawnAt > 0)  s.nextBossSpawnAt += gap;
      if (s.tierBossWarning)      s.tierBossWarning.startTime += gap;
      pausedAtRef.current = null;
    }
  }, [gameState]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ref for ESC key callback — updated on every render (always current gameState)
  const onEscapeRef = useRef(null);
  onEscapeRef.current = () => {
    setGameState(prev => {
      if (prev === 'playing' || prev === 'levelup') return 'paused';
      if (prev === 'paused') {
        lastSpawnRef.current = performance.now();
        return 'playing';
      }
      return prev;
    });
  };

  // ── Potion use callback ────────────────────────────────────────────────────
  // Updated each render so the keyboard handler always sees the current potionCount
  // without needing to re-register event listeners.
  const onPotionUseRef = useRef(null);
  onPotionUseRef.current = () => {
    const s   = stateRef.current;
    const now = performance.now();
    if (!s.alive) return;
    if ((s.potionCount ?? 0) <= 0) return;
    if (now - (s.lastPotionTime ?? -POTION_COOLDOWN) < POTION_COOLDOWN) return;
    const healAmt    = Math.round((s.maxHp ?? 100) * POTION_HEAL_PCT);
    s.hp             = Math.min(s.maxHp ?? 100, s.hp + healAmt);
    s.lastPotionTime = now;
    s.potionCount    = (s.potionCount ?? 1) - 1;
    consumePotion(); // persist decrement to localStorage
    s.floatingTexts.push({
      id:        now + 0.1,
      x:         s.player.x + PLAYER_SIZE / 2,
      y:         s.player.y - 10,
      text:      `+${healAmt} HP`,
      color:     '#44ff88',
      startTime: now,
      duration:  1200,
    });
  };

  // ── Device / browser back-button interception ────────────────────────────
  // Uses the same "ref updated every render" pattern as onTabHiddenRef so the
  // popstate listener never needs to be re-registered, yet always sees the
  // latest gameState.
  const onBackRef = useRef(null);
  onBackRef.current = () => {
    if (['playing', 'levelup', 'magicselect', 'modifier', 'chestopen'].includes(gameState)) {
      // Game is running — pause it
      setGameState('paused');
    } else if (gameState === 'paused') {
      // Already at pause screen; do nothing (player can use the on-screen buttons)
    } else if (['character', 'talents', 'enhancement', 'leaderboard', 'dead'].includes(gameState)) {
      // Secondary screens → go up one level to idle menu
      setGameState('idle');
    } else {
      // idle or landing → ask whether to leave the app
      setShowQuitModal(true);
    }
  };

  useEffect(() => {
    // Seed history with a sentinel entry so there is always one to pop before
    // the browser would navigate away.
    window.history.pushState({ gameBack: true }, '');
    const handlePopState = () => {
      // Immediately re-push so the next back press is also intercepted.
      window.history.pushState({ gameBack: true }, '');
      onBackRef.current?.();
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []); // register once — ref always holds the latest callback

  // ── When game ends: save run stats ────────────────────────────────────────
  useEffect(() => {
    if (gameState === 'dead') {
      const s     = stateRef.current;
      const drops = s.droppedItems ?? [];
      const gold  = s.goldThisRun  ?? 0;
      const kills = s.kills        ?? 0;
      // Levels earned this run = levels gained above the starting player level
      const levelsEarned = Math.max(0, (s.level ?? playerLevel) - playerLevel);

      setSessionDrops(drops);
      setSessionKills(kills);
      setSessionGold(gold);

      if (drops.length > 0)  addDroppedItems(drops);
      if (gold > 0)           addGold(gold);
      if (levelsEarned > 0) {
        addTalentPoints(levelsEarned);   // 1 talent pt per level-up
        addPlayerLevel(levelsEarned);    // bank levels into persistent player level
      }

      saveLastDeathTier(s.currentTier ?? 0);

      addRunToLeaderboard({
        score: s.score,
        level: s.level,
        kills,
        gold,
        date: new Date().toLocaleDateString(),
      });
    }
  }, [gameState]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Actions ────────────────────────────────────────────────────────────────
  const startGame = useCallback(() => {
    const init = createInitialState();
    // Start at persistent player level with correctly scaled XP requirement
    init.level       = playerLevel;
    init.xpToNext    = Math.ceil(5 * Math.pow(1.08, playerLevel - 1));
    init.potionCount = potionCount; // carry potions into the run
    init.lootFilter  = lootFilter;  // carry loot filter into the run
    applyEquippedItems(init, equipped);
    applyTalents(init, activatedTalents);
    Object.assign(stateRef.current, init);
    setAttackRenderKey(0);
    setMagicSelectSpells({ spells: [], isUpgrade: false });
    setSessionDrops([]);
    setSessionKills(0);
    setSessionGold(0);
    lastSpawnRef.current = performance.now();
    startTimeRef.current = performance.now();
    pausedAtRef.current  = null;    // discard any leftover pause offset from previous session
    setGameState('playing');
  }, [equipped, activatedTalents, playerLevel, potionCount, lootFilter]);

  const startFromTier = useCallback((tier) => {
    const init = createInitialState();
    init.level       = playerLevel;
    init.xpToNext    = Math.ceil(5 * Math.pow(1.08, playerLevel - 1));
    init.currentTier = tier;
    init.potionCount = potionCount; // carry potions into the run
    init.lootFilter  = lootFilter;  // carry loot filter into the run
    applyEquippedItems(init, equipped);
    applyTalents(init, activatedTalents);
    Object.assign(stateRef.current, init);
    setAttackRenderKey(0);
    setMagicSelectSpells({ spells: [], isUpgrade: false });
    setSessionDrops([]);
    setSessionKills(0);
    setSessionGold(0);
    lastSpawnRef.current = performance.now();
    // Offset startTime so the game loop thinks `tier * 30s` have already elapsed
    startTimeRef.current = performance.now() - tier * 30 * 1000;
    pausedAtRef.current  = null;    // discard any leftover pause offset from previous session
    setGameState('playing');
  }, [equipped, activatedTalents, playerLevel, potionCount, lootFilter]);

  const resumeGame = useCallback((skill) => {
    skill.apply(stateRef.current);
    lastSpawnRef.current = performance.now();
    setGameState('playing');
  }, []);

  const resumeFromPause = useCallback(() => {
    lastSpawnRef.current = performance.now();
    setGameState('playing');
  }, []);

  // Called when player picks a world modifier from the overlay
  const resumeFromModifier = useCallback((modifierObj) => {
    stateRef.current.activeModifier = modifierObj;
    lastSpawnRef.current = performance.now();
    setWorldModifierChoices([]);
    setGameState('playing');
  }, []);

  const applyResumeKnockback = useCallback((s) => {
    const pcx = s.player.x + PLAYER_SIZE / 2;
    const pcy = s.player.y + PLAYER_SIZE / 2;
    const RADIUS = 75;
    const PUSH   = 50;
    for (const e of s.enemies) {
      const sz  = e.size ?? ENEMY_SIZE;
      const ecx = e.x + sz / 2;
      const ecy = e.y + sz / 2;
      const dist = Math.hypot(ecx - pcx, ecy - pcy);
      if (dist < RADIUS && dist >= 1) {
        e.x += ((ecx - pcx) / dist) * PUSH;
        e.y += ((ecy - pcy) / dist) * PUSH;
      }
    }
  }, []);

  // Called when player picks a spell or upgrade from the magic tier overlay
  const resumeFromMagicSelect = useCallback((spellId, isUpgrade) => {
    const s = stateRef.current;
    if (isUpgrade) {
      // Increment the spell's level (capped at 3)
      const levels = s.runSpellLevels ?? {};
      s.runSpellLevels = { ...levels, [spellId]: Math.min(3, (levels[spellId] ?? 1) + 1) };
    } else {
      // Add to next free slot
      const existing = s.runSpells ?? [];
      s.runSpells = existing.length < 4
        ? [...existing, spellId]
        : [...existing.slice(1), spellId];
      // Initialise level tracking for this spell
      if (!s.runSpellLevels) s.runSpellLevels = {};
      if (!s.runSpellLevels[spellId]) s.runSpellLevels[spellId] = 1;
    }
    applyResumeKnockback(s);
    lastSpawnRef.current = performance.now();
    setGameState('playing');
  }, [applyResumeKnockback]);

  // Called when the player picks an item from the chest loot overlay
  const resumeFromChestLoot = useCallback((item) => {
    const s = stateRef.current;
    // Add selected item to this run's loot
    s.droppedItems.push(item);

    if (s.isTierBossChest) {
      // Tier boss chest: announce the new environment, no enemy ring spawn
      const newEnv = s.environment ?? 'forest';
      setEnvAnnouncement(newEnv);
      setTimeout(() => setEnvAnnouncement(null), 3500);
    } else {
      // Regular chest: spawn 40 enemies in a ring around the player
      const pcx     = s.player.x + PLAYER_SIZE / 2;
      const pcy     = s.player.y + PLAYER_SIZE / 2;
      const ringR   = Math.max(WORLD_WIDTH, WORLD_HEIGHT) * 0.65;
      const tierIdx = s.currentTier ?? 0;
      const baseMaxHp = 5 * (tierIdx + 1);
      const mod     = s.activeModifier;
      const hpMult  = mod?.enemyHpMult ?? 1;
      for (let i = 0; i < 40; i++) {
        const angle = (i / 40) * Math.PI * 2;
        s.enemies.push({
          x:         pcx + Math.cos(angle) * ringR - ENEMY_SIZE / 2,
          y:         pcy + Math.sin(angle) * ringR - ENEMY_SIZE / 2,
          hp:        Math.round(baseMaxHp * hpMult),
          maxHp:     Math.round(baseMaxHp * hpMult),
          damage:    ENEMY_DAMAGE,
          isBoss:    false,
          isTierBoss: false,
          tierIdx,
          armor:     0,
        });
      }
    }

    applyResumeKnockback(s);
    setChestLootOptions([]);
    lastSpawnRef.current = performance.now();
    setGameState('playing');
  }, [applyResumeKnockback]); // eslint-disable-line react-hooks/exhaustive-deps

  const goToMenu = useCallback(() => setGameState('idle'), []);

  // Activate a talent node: deduct talent points, record node ID
  const handleActivateTalent = useCallback((nodeId, cost) => {
    spendTalentPoints(cost);
    activateTalent(nodeId);
  }, [spendTalentPoints, activateTalent]);

  // Reset all talents: TalentTreeScreen computes exact point refund and passes it in
  const handleResetTalents = useCallback((refundPoints) => {
    resetTalents();
    if (refundPoints > 0) addTalentPoints(refundPoints);
  }, [resetTalents, addTalentPoints]);

  const handleSellItem = useCallback((item, index) => {
    const sold = sellItem(index);
    if (sold) addGold(getSellPrice(sold));
  }, [sellItem, addGold]);

  const handleSellAll = useCallback((slotTypeFilter) => {
    const sold = sellAllItems(slotTypeFilter);
    if (sold.length > 0) {
      const total = sold.reduce((sum, it) => sum + getSellPrice(it), 0);
      addGold(total);
    }
  }, [sellAllItems, addGold]);

  const handleSellAllRunes = useCallback(() => {
    const sold = sellAllRunes();
    if (sold.length > 0) {
      const total = sold.reduce((sum, it) => sum + getSellPrice(it), 0);
      addGold(total);
    }
  }, [sellAllRunes, addGold]);

  // ── Keyboard hook ──────────────────────────────────────────────────────────
  const { triggerAttack, triggerDash, triggerCast, triggerPause, triggerBlock, triggerPotion } =
    useKeyboard({ stateRef, setAttackRenderKey, onEscapeRef, onPotionUseRef, gameWorldRef });

  useGameLoop({
    gameState,
    stateRef,
    lastSpawnRef,
    startTimeRef,
    setGameState,
    setRenderTick,
    setAttackRenderKey,
    setLevelUpSkills,
    setMagicSelectSpells,
    setWorldModifierChoices,
    setChestLootOptions,
  });

  // ── Derived render values ──────────────────────────────────────────────────
  const s = stateRef.current;
  const now = performance.now();
  const isInvincible = (gameState === 'playing' || gameState === 'paused') &&
    (now - s.lastHitTime < 1500 || now < (s.shieldUntil ?? 0));

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="App">
      <span className="build-version">alphaBuild 0.2.1</span>
      {gameState === 'landing' && (
        <LandingScreen onEnter={() => setGameState('idle')} />
      )}

      {gameState === 'idle' && (
        <IdleScreen
          onStart={startGame}
          onStartFromTier={startFromTier}
          lastDeathTier={lastDeathTier}
          onCharacter={() => setGameState('character')}
          onShop={() => setGameState('talents')}
          onEnhancement={() => setGameState('enhancement')}
          onLeaderboard={() => setGameState('leaderboard')}
          onSettings={() => setShowSettings(true)}
        />
      )}

      {gameState === 'character' && (
        <CharacterScreen
          onBack={() => setGameState('idle')}
          inventory={inventory}
          equipped={equipped}
          onEquip={equipItem}
          onUnequip={unequipItem}
          onSell={handleSellItem}
          onSellAll={handleSellAll}
          onSellAllRunes={handleSellAllRunes}
          onLock={lockItem}
          onEquipRuneWord={equipRuneWord}
          onUnequipAllRunes={unequipAllRunes}
          totalGold={totalGold}
          playerLevel={playerLevel}
          activatedTalents={activatedTalents}
        />
      )}

      {gameState === 'enhancement' && (
        <EnhancementScreen
          onBack={() => setGameState('idle')}
          inventory={inventory}
          onEnhance={enhanceItem}
          spendGold={spendGold}
          totalGold={totalGold}
          playerLevel={playerLevel}
          potionCount={potionCount}
          onCraftPotion={() => {
            if (totalGold >= POTION_COST && potionCount < POTION_MAX) {
              spendGold(POTION_COST);
              addPotion();
            }
          }}
        />
      )}

      {gameState === 'talents' && (
        <TalentTreeScreen
          activatedTalents={activatedSet}
          talentPoints={talentPoints}
          onActivate={handleActivateTalent}
          onReset={handleResetTalents}
          onBack={() => setGameState('idle')}
        />
      )}

      {gameState === 'leaderboard' && (
        <LeaderboardScreen
          leaderboard={leaderboard}
          totalGold={totalGold}
          onBack={() => setGameState('idle')}
        />
      )}

      {gameState === 'dead' && (
        <DeadScreen
          score={s.score}
          level={s.level}
          kills={sessionKills}
          gold={sessionGold}
          droppedItems={sessionDrops}
          onRestart={startGame}
          onMenu={goToMenu}
        />
      )}

      {/* ── Dash Quest ──────────────────────────────────────────────────────── */}
      {(gameState === 'playing' || gameState === 'levelup' || gameState === 'paused' || gameState === 'magicselect' || gameState === 'modifier' || gameState === 'chestopen') && (
        <GameWorld
          s={s}
          gameState={gameState}
          attackRenderKey={attackRenderKey}
          levelUpSkills={levelUpSkills}
          isInvincible={isInvincible}
          onSkillChoose={resumeGame}
          onResume={resumeFromPause}
          onMenu={goToMenu}
          onSettings={() => setShowSettings(true)}
          magicSelectSpells={magicSelectState.spells}
          magicSelectIsUpgrade={magicSelectState.isUpgrade}
          onMagicSpellChoose={resumeFromMagicSelect}
          worldModifierChoices={worldModifierChoices}
          onModifierChoose={resumeFromModifier}
          chestLootOptions={chestLootOptions}
          onChestLootChoose={resumeFromChestLoot}
          envAnnouncement={envAnnouncement}
          gameWorldRef={gameWorldRef}
          onMobileAttack={triggerAttack}
          onMobileDash={triggerDash}
          onMobileCast={triggerCast}
          onMobilePause={triggerPause}
          onMobileBlock={triggerBlock}
          onMobilePotion={triggerPotion}
        />
      )}

      {/* ── Quit-game confirmation (device back on idle / landing) ────────── */}
      {showQuitModal && (
        <div className="quit-modal-backdrop" onClick={() => setShowQuitModal(false)}>
          <div className="quit-modal" onClick={e => e.stopPropagation()}>
            <h2 className="quit-modal-title">Leave Game?</h2>
            <p className="quit-modal-sub">Your progress is saved.</p>
            <div className="quit-modal-buttons">
              <button
                className="quit-modal-btn quit-modal-btn--stay"
                onClick={() => setShowQuitModal(false)}
              >
                Stay
              </button>
              <button
                className="quit-modal-btn quit-modal-btn--leave"
                onClick={() => window.close()}
              >
                Leave
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Settings overlay (floats above idle or paused game) ─────────── */}
      {showSettings && (
        <SettingsScreen
          musicVolume={musicVolume}
          musicMuted={musicMuted}
          onVolumeChange={setMusicVolume}
          onMuteToggle={toggleMusicMute}
          lootFilter={lootFilter}
          onLootFilterChange={setLootFilter}
          onClose={() => setShowSettings(false)}
        />
      )}
    </div>
  );
}

export default App;
